import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { ConfirmationDialogWithIcon, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { FCSecurePage, useShopPersistence, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import OperationModesSchedulesInner from './components/operationModesSchedules/OperationModesSchedules';
import { operationModesSchedulesService, ShopAutomationModule } from '@/services/operationModesSchedulesService';
import type { OperationMode } from '@/services';
import { OperationModeSchedule, OperationModesScheduleGroup } from '@features/shopautomation/types.ts';
import ToggleButton from '@/components/shared/ToggleButton';
import { useApiStore, useAuthStore, useSettingsStore, useStatusDialogStore } from '@core/store';
import { Role } from '@core/types';
import { useMobileHeaderPanelContent } from '@layouts/MobileHeaderPanel.tsx';
import { DataTableColumn } from '@/components/datatable/tableTypes.ts';
import NewWcnTable from '@/components/datatable/NewWcnTable.tsx';
import { apiClient } from '@core/api';
import { notificationService } from '@core/notifications/NotificationService';
import { formatDate, formatTime } from '@features/administration/components/operationModesSchedules/scheduleDisplay.ts';
import StatusTag from '@/components/shared/Tags.tsx';
import BaseSelector from '@/components/selectors/BaseSelector.tsx';
import type { ConfirmationDialogRef } from '@/components/dialog/ConfirmationDialog.tsx';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import ManageOperationMode from '@features/administration/components/operationModesSchedules/ManageOperationMode.tsx';
import { getActiveOnTranslation } from '@features/shopautomation/operationModesSchedulesUtils.ts';
import { ShopStatusResponse } from '@features/shopautomation/ShopAutomationShops.tsx';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';

const CHANGE_OPERATION_MODE_ERRORS = {
    gatewayOffline: 'gatewayOffline',
    unknownError: 'unknownError',
    noConnectionToBroker: 'noConnectionToBroker',
    operationModeChangeInvalid: 'operationModeChangeInvalid',
    sameMandatoryDeviceOffline: 'sameMandatoryDeviceOffline',
    differentMandatoryDeviceOffline: 'differentMandatoryDeviceOffline',
    deviceTimedOut: 'deviceTimedOut'
} as const;

const OperationModesSchedules: FCSecurePage = () => {
    const { t } = useTranslation();
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const { hasRole } = useAuthStore();
    const { isMobile } = useSettingsStore();

    const [schedules, setSchedules] = useState<OperationModesScheduleGroup[]>([]);
    const [activeOperationModes, setActiveOperationModes] = useState<string[]>([]);
    const [currentModule, setCurrentModule] = useState<ShopAutomationModule | null>(null);
    const [schedulesEnabled, setSchedulesEnabled] = useState(true);
    const [isOperationModeScheduleActive, setIsOperationModeScheduleActive] = useState(true);
    const [togglingEnabled, setTogglingEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const { shopIds, shopAutomationIds, operationModes, loadShopIds, loadOperationModes } = useApiStore();
    const [shopData, setShopData] = useState<ShopStatusResponse[]>([]);
    const { notifySuccess, notifyError, notifyErrorResponse } = useNotifications();

    const { showStatusSuccess, showStatusError } = useStatusDialogStore();
    const getModeTagState = useModeTagState();

    const [changingOperationMode, setChangingOperationMode] = useState(false);
    const [currentScheduledState, setCurrentScheduledState] = useState<OperationModeSchedule | undefined>(undefined);
    const [upcomingScheduledState, setUpcomingScheduledState] = useState<OperationModeSchedule | undefined>(undefined);
    const [loadingScheduledInfo, setLoadingScheduledInfo] = useState(false);

    const [dialogHeading, setDialogHeading] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const confirmScheduleActiveDialogRef = useRef<ConfirmationDialogRef>(null);
    const confirmScheduleEnableDialogRef = useRef<ConfirmationDialogRef>(null);

    const shopId = selectedShopIds[0] ?? null;

    const openEditorRef = useRef<(() => void) | null>(null);

    const filteredShopIds = useMemo(
        () => shopIds.filter((s) => shopAutomationIds.some((auto) => auto.shopId === s.id)),
        [shopIds, shopAutomationIds]
    );

    const shop = filteredShopIds.find((s) => s.id === shopId);

    const canToggleSchedules = hasRole(Role.SuperAdmin);
    const canEditSchedules = hasRole(Role.StoreAdmin);

    const load = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const [{ schedules: data, activeOperationModes: activeModes, module }] = await Promise.all([
                operationModesSchedulesService.loadAll(shopId),
                loadShopStatus(),
                loadOperationModes()
            ]);
            setSchedules(data);
            setActiveOperationModes(activeModes);
            setCurrentModule(module);
            setSchedulesEnabled(!module?.hasDisabledOperationModeSchedules);
            setIsOperationModeScheduleActive(module?.isOperationModeScheduleActive);
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error('[OperationModesSchedules] Failed to load:', error);
        } finally {
            setLoading(false);
        }
    }, [shopId]);

    const loadShopStatus = useCallback(async () => {
        try {
            const response = await apiClient.get<ShopStatusResponse[]>('shop/status');
            setShopData(response.data || []);
            setLastUpdate(DateTime.now());
        } catch (error: any) {
            notificationService.error(t('error'), t('errors.loadingData'));
            setShopData([]);
            console.error('Failed to load shop status data:', error);
        }
    }, [selectedShopIds[0], t]);

    useRegisterRefresh(load, loading);

    useEffect(() => {
        load();

        if (!shopIds || shopIds.length === 0) {
            loadShopIds();
        }
    }, [load]);

    const handleSchedulesChange = useCallback(
        async (updated: OperationModesScheduleGroup[], schedule: OperationModesScheduleGroup, action: string) => {
            if (!currentModule) return;
            setLoading(true);
            const heading =
                action === 'add'
                    ? t('newOperationModeSchedule')
                    : action === 'update'
                      ? t('updateOperationModeSchedule')
                      : t('deleteOperationModeSchedule');
            try {
                const conflictingSchedules = (currentModule.operationModesSchedules || [])
                    .flatMap((scheduleGroup: any) => scheduleGroup.operationModeSchedules || [])
                    .filter(
                        (schedule: any) =>
                            schedule.operationMode &&
                            !(currentModule.activeOperationModes || []).includes(String(schedule.operationMode))
                    );
                if (conflictingSchedules.length > 0) {
                    const conflictDetails = conflictingSchedules
                        .map((schedule: any) => `• ${schedule.operationMode} (from: ${schedule.from})`)
                        .join('<br>');
                    notifyError(t('errors.invalidOperationModeConfiguration', { 0: conflictDetails }));
                    return;
                }

                await operationModesSchedulesService.save(currentModule, updated);

                showStatusSuccess({
                    heading: heading,
                    message: t(
                        action === 'delete' ? 'deleteOperationModeScheduleMessage' : 'newOperationModeScheduleMessage',
                        {
                            name: schedule.name || getActiveOnTranslation(schedule, t),
                            shop: shop?.name ?? ''
                        }
                    )
                });

                await load();
            } catch (error) {
                console.error('[OperationModesSchedules] Failed to save schedules:', error);
                showStatusError({
                    heading: heading,
                    message: t('errors.unknownError')
                });
            } finally {
                setLoading(false);
            }
        },
        [currentModule]
    );

    const handleSchedulesEnabledChange = useCallback(
        async (enabled: boolean) => {
            if (!currentModule) return;
            if (enabled) {
                await toggleSchedulesEnabled();
            } else {
                confirmScheduleEnableDialogRef.current?.openDialog();
            }
        },
        [currentModule, schedules]
    );

    const toggleSchedulesEnabled = useCallback(async () => {
        if (!currentModule) return;
        setTogglingEnabled(true);
        const updatedModule: ShopAutomationModule = {
            ...currentModule,
            hasDisabledOperationModeSchedules: !currentModule.hasDisabledOperationModeSchedules,
            isOperationModeScheduleActive: currentModule.hasDisabledOperationModeSchedules
                ? currentModule.isOperationModeScheduleActive
                : false
        };
        try {
            await operationModesSchedulesService.save(updatedModule, schedules);
            await load();
            notifySuccess();
        } catch (error) {
            console.error('[OperationModesSchedules] Failed to save schedules enabled state:', error);
            notifyErrorResponse(error);
        } finally {
            setTogglingEnabled(false);
        }
    }, [currentModule, schedules]);

    const handleActiveOperationMode = (enabled: boolean) => {
        if (!currentModule) return;
        if (enabled) {
            const isProduction = shop?.isProduction === true;
            const message = getScheduleActiveDialogMessage(isProduction);
            setDialogHeading(t('omSchedulesActivationHeading'));
            setDialogMessage(message);
        } else {
            setDialogHeading(t('omSchedulesDeactivationHeading'));
            setDialogMessage(t('omSchedulesDeactivationMessage'));
        }
        confirmScheduleActiveDialogRef.current?.openDialog();
    };

    const toggleScheduleActivation = async () => {
        if (!currentModule) return;

        setLoading(true);
        const updatedModule: ShopAutomationModule = {
            ...currentModule,
            isOperationModeScheduleActive: !currentModule?.isOperationModeScheduleActive
        };
        try {
            await operationModesSchedulesService.save(updatedModule, schedules);
            await load();
        } catch (error) {
            console.error('[OperationModesSchedules] Failed to save schedules enabled state:', error);
        } finally {
            setLoading(false);
        }
    };

    const getScheduleActiveDialogMessage = (isProduction: boolean): string => {
        if (!isProduction) return t('omSchedulesActivationIsProductionFalseMessage');
        if (!currentModule) return t('omSchedulesActivationMessageFallback');

        if (!currentScheduledState) return t('omSchedulesActivationMessageFallback');
        if (!upcomingScheduledState)
            return t('omSchedulesActivationMessageWithoutNext', {
                currentOperationMode: currentScheduledState.operationMode
            });

        return t('omSchedulesActivationMessage', {
            currentOperationMode: currentScheduledState.operationMode,
            nextOperationMode: upcomingScheduledState.operationMode,
            time: upcomingScheduledState.from
        });
    };

    const loadScheduledOperationModes = useCallback(async () => {
        const deviceId = (currentModule as any)?.gateway?.deviceId ?? (currentModule as any)?.deviceId;
        const moduleId = (currentModule as any)?.moduleId ?? currentModule?.id;
        if (!deviceId || !moduleId) return;

        setLoadingScheduledInfo(true);
        try {
            const response = await apiClient.get<{
                currentlyScheduledOperationMode: { operationMode: string; from: string } | null;
                nextScheduledOperationMode: { operationMode: string; from: string; date: string } | null;
            }>(`shopAutomation/${deviceId}/${moduleId}/scheduledOperationModes`);

            const { currentlyScheduledOperationMode, nextScheduledOperationMode } = response.data;

            setCurrentScheduledState(
                currentlyScheduledOperationMode
                    ? {
                          operationMode: currentlyScheduledOperationMode.operationMode,
                          from: formatTime(currentlyScheduledOperationMode.from)
                      }
                    : undefined
            );
            setUpcomingScheduledState(
                nextScheduledOperationMode
                    ? {
                          operationMode: nextScheduledOperationMode.operationMode,
                          from: formatTime(nextScheduledOperationMode.from),
                          date: formatDate(nextScheduledOperationMode.date)
                      }
                    : undefined
            );
        } catch (error) {
            console.error('[OperationModesSchedules] Failed to load scheduled operation modes:', error);
        } finally {
            setLoadingScheduledInfo(false);
        }
    }, [currentModule]);

    useEffect(() => {
        if (schedulesEnabled && currentModule) {
            loadScheduledOperationModes();
        }
    }, [schedulesEnabled, currentModule]);

    const getShopOperationMode = (shopId: string): string | undefined => {
        return shopData.find((s) => s.id === shopId)?.operationModeState?.currentOperationMode;
    };

    const handleChangeOperationMode = useCallback(
        async (operationMode: string) => {
            const deviceId = (currentModule as any)?.gateway?.deviceId ?? (currentModule as any)?.deviceId;
            const moduleId = (currentModule as any)?.moduleId ?? currentModule?.id;
            if (!deviceId || !moduleId) return;

            setChangingOperationMode(true);
            try {
                await apiClient.post(
                    `shopAutomation/${deviceId}/${moduleId}/operationMode/?operationMode=${operationMode}`
                );

                if (currentModule?.isOperationModeScheduleActive) {
                    try {
                        const updatedItem = { ...currentModule, isOperationModeScheduleActive: false };
                        await apiClient.put(`shopAutomation/${currentModule.id}`, updatedItem);
                        await load();

                        notificationService.warning(t('warning'), t('manualOperationModeChangeWarning'));
                    } catch (scheduleError) {
                        console.error('Failed to turn off schedule:', scheduleError);
                        notificationService.warning(
                            t('warning'),
                            'Operation mode changed but schedule is still active'
                        );
                    }
                }

                setShopData((prev) =>
                    prev.map((shop) =>
                        shop.id === shopId
                            ? {
                                  ...shop,
                                  operationModeState: {
                                      currentOperationMode: operationMode,
                                      operationModeScheduleActive:
                                          shop.operationModeState?.operationModeScheduleActive ?? false
                                  }
                              }
                            : shop
                    )
                );

                notificationService.success(t('successful'));
            } catch (error: any) {
                const errorMessage = error?.message;
                const hasCustomErrorMessage = Object.values(CHANGE_OPERATION_MODE_ERRORS).includes(errorMessage);

                if (hasCustomErrorMessage) {
                    notificationService.error(
                        t('errors.operationModeChangeNotPossible'),
                        t(`errors.${errorMessage}`, {
                            targetOperationMode: error?.properties?.targetOperationMode,
                            targetFallbackOperationMode: error?.properties?.fallbackOperationMode,
                            unhealthyMandatoryDevices: error?.properties?.unhealthyMandatoryDevices
                        })
                    );
                } else {
                    notificationService.error(t('error'), t('errors.unknownError'));
                    console.error('Failed to change operation mode:', error);
                }
            } finally {
                setChangingOperationMode(false);
            }
        },
        [currentModule, t]
    );

    const filterContent = useMemo(
        () => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BaseSelector
                    value={shopId}
                    items={filteredShopIds}
                    itemText="name"
                    itemValue="id"
                    label={t('shop')}
                    placeholder={t('selectAShop')}
                    onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                    searchable
                />

                {shopId && shop && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ToggleButton
                            checked={schedulesEnabled}
                            onChange={handleSchedulesEnabledChange}
                            loading={togglingEnabled}
                            disabled={!canToggleSchedules}
                        />
                        <Typography
                            sx={{
                                color: '#000',
                                fontFamily: 'Wanzl_Etelka Medium',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400
                            }}
                        >
                            {t(schedulesEnabled ? 'schedulesEnabled' : 'schedulesDisabled')}
                        </Typography>
                    </Box>
                )}
            </Box>
        ),
        [
            shopId,
            setSelectedShopIds,
            currentModule,
            schedulesEnabled,
            handleSchedulesEnabledChange,
            togglingEnabled,
            canToggleSchedules,
            t
        ]
    );

    useMobileHeaderPanelContent(filterContent, { title: t('filter') });

    const renderOperationModeContent = () => {
        if (!shopId || !shop) {
            return '';
        }
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress size={32} thickness={4} />
                </Box>
            );
        }
        return (
            <>
                <Box sx={{ px: 3 }}>
                    <ManageOperationMode
                        schedulesEnabled={schedulesEnabled}
                        isOperationModeScheduleActive={isOperationModeScheduleActive}
                        onActiveOperationMode={handleActiveOperationMode}
                        activeOperationModes={activeOperationModes}
                        onChangeOperationMode={handleChangeOperationMode}
                        loadingScheduledInfo={loadingScheduledInfo}
                        disabled={togglingEnabled || changingOperationMode}
                        getShopOperationMode={getShopOperationMode}
                        shopId={selectedShopIds[0]}
                        currentScheduledState={currentScheduledState}
                        upcomingScheduledState={upcomingScheduledState}
                    />
                </Box>

                <Box sx={{ px: 3, pb: 3 }}>
                    <NewWcnTable
                        columns={columns}
                        data={operationModes.filter((mode) => activeOperationModes.includes(mode.name))}
                        loading={loading}
                        noDataMessage={t('noData')}
                        size="small"
                        mobileView="card"
                    />
                </Box>
            </>
        );
    };

    const renderSchedulesContent = () => {
        if (!shopId || !shop) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                        {t('noData')}
                    </Typography>
                </Box>
            );
        }
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress size={32} thickness={4} />
                </Box>
            );
        }
        return (
            <OperationModesSchedulesInner
                value={schedules}
                operationModes={operationModes}
                activeOperationModes={activeOperationModes}
                onChange={handleSchedulesChange}
                schedulesEnabled={schedulesEnabled && canEditSchedules}
                openEditorRef={openEditorRef}
            />
        );
    };

    const columns: DataTableColumn<OperationMode>[] = useMemo(
        () => [
            {
                text: t('operationMode'),
                value: 'name',
                sortable: false,
                width: 200,
                render: (row) => <StatusTag state={getModeTagState(row.name)} />
            },
            {
                text: t('description'),
                value: 'description',
                sortable: false,
                width: 400,
                render: (row) => row.description || t(`descriptions.${row.name}`)
            }
        ],
        [t, operationModes]
    );

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi-calendar4-range" />}
                title={t('descriptions.administrationOperationModesSchedules')}
                menus={getMenuData()}
                iconSize="16px"
                currentRoute={{ path: '/manage-shop/operationModesSchedules' }}
                t={t}
                lastUpdate={lastUpdate}
            />
            <Box sx={{ px: 3, pb: 3 }}>
                {!isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <Box>
                            <BaseSelector
                                value={shopId}
                                items={filteredShopIds}
                                itemText="name"
                                itemValue="id"
                                label={t('shop')}
                                placeholder={t('selectAShop')}
                                onChange={(id) => setSelectedShopIds(id ? [id] : [])}
                                searchable
                            />
                        </Box>

                        {shopId && shop && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '40px', mt: '20px' }}>
                                <ToggleButton
                                    checked={schedulesEnabled}
                                    onChange={handleSchedulesEnabledChange}
                                    loading={togglingEnabled}
                                    disabled={!canToggleSchedules}
                                />
                                <Typography
                                    sx={{
                                        color: '#000',
                                        fontFamily: 'Wanzl_Etelka Medium',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400
                                    }}
                                >
                                    {t(schedulesEnabled ? 'schedulesEnabled' : 'schedulesDisabled')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {renderOperationModeContent()}

            <Box sx={{ px: 4, pb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '56px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            className="new-page-title-bar-icon"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                lineHeight: 1,
                                '& i': {
                                    fontSize: '16px !important',
                                    color: '#000000 !important'
                                },
                                marginBottom: 0.1
                            }}
                        >
                            <i className="bi-calendar4-range" />
                        </Box>

                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                letterSpacing: '0.05em',
                                fontFamily: 'var(--font-family-medium)',
                                color: '#000000',
                                textTransform: 'uppercase'
                            }}
                        >
                            {t('schedules')}
                        </Typography>

                        <Tooltip
                            title={
                                <Typography component="span" sx={{ whiteSpace: 'pre-line' }}>
                                    {t('operationModesSchedulesTooltip')}
                                </Typography>
                            }
                            placement="right"
                            arrow
                            classes={{ tooltip: 'page-title-tooltip' }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        { name: 'offset', options: { offset: [0, 16] } },
                                        {
                                            name: 'preventOverflow',
                                            enabled: true,
                                            options: { boundary: 'viewport', altAxis: true }
                                        },
                                        {
                                            name: 'flip',
                                            enabled: true,
                                            options: { fallbackPlacements: ['right', 'top', 'bottom'] }
                                        }
                                    ]
                                },
                                tooltip: { sx: { maxWidth: `300px !important` } }
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{ '& i': { fontSize: '16px !important', color: 'var(--gray-medium)' } }}
                            >
                                <i className="bi bi-question-circle" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box
                    className="new-page-title-bar-border"
                    sx={{
                        height: '1px',
                        bgcolor: 'var(--sidebar-divider)',
                        mx: -0.85
                    }}
                />
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="text"
                        onClick={() => openEditorRef.current?.()}
                        disabled={(!schedulesEnabled && schedules.length > 0) || !currentModule || !canEditSchedules}
                        className="pagetitlebar-action-btn pagetitlebar-action-btn--schedule"
                        disableRipple
                    >
                        <i
                            className="bi bi-plus-lg"
                            style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                        />
                        <span>{t('addOperationModeSchedule')}</span>
                    </Button>
                </Box>

                {renderSchedulesContent()}

                <ConfirmationDialogWithIcon
                    dialogRef={confirmScheduleActiveDialogRef}
                    heading={dialogHeading}
                    message={dialogMessage}
                    onConfirm={toggleScheduleActivation}
                    icon={<i className="bi bi-shuffle" />}
                    cancelText="noCancel"
                />

                <ConfirmationDialogWithIcon
                    dialogRef={confirmScheduleEnableDialogRef}
                    heading="omSchedulesDisableHeading"
                    message="omSchedulesDisableMessage"
                    onConfirm={toggleSchedulesEnabled}
                    icon={<i className="bi bi-shuffle" />}
                    cancelText="noCancel"
                />
            </Box>
        </Box>
    );
};

OperationModesSchedules.displayName = 'Operation Modes Schedules';
export default OperationModesSchedules;
